import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {List} from 'immutable';

import type {ClassroomModelV1} from '@albert-io/models';
import {BREAKPOINTS} from 'client/constants';

// Below the `m` breakpoint the expanded sidemenu (296px) would consume most of the content area, so
// it starts collapsed there — still expandable. Guarded: jsdom has no matchMedia.
const startsCollapsed = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  !window.matchMedia(`(min-width: ${BREAKPOINTS.m}px)`).matches;

type State = {
  archivedClassrooms: List<ClassroomModelV1>;
  areArchivedClassroomsLoading: boolean;
  // The user id the archived list was last loaded for (null = never). Lets Home gate its first paint on
  // the archived list during auto-archive season so the notice doesn't pop in late (#19169), and — being
  // keyed by identity — re-fetches after a masquerade switch instead of stranding the prior teacher's list.
  archivedClassroomsLoadedFor: string | null;
  areClassroomsLoading: boolean;
  // True once the active-classroom fetch has settled at least once. Lets Home show a first-load state
  // without re-blanking on every subsequent refetch (which flips areClassroomsLoading per fetch).
  hasLoadedClassrooms: boolean;
  classrooms: List<ClassroomModelV1>;
  isShowingArchivedClassrooms: boolean;
  isSidemenuCollapsed: boolean;
  // The class whose context the sidemenu should keep showing on scope-neutral routes (e.g. Home).
  selectedClassId: string | null;
  // The effective (masquerade-aware) user id the remembered selection belongs to. The module store
  // outlives masquerade transitions (no page reload), so without this a re-entered masquerade session
  // inherits the previous session's class — which the roster check can't catch when it's the same
  // teacher (#19443).
  selectionOwnerId: string | null;
};

type Actions = {
  setArchivedClassrooms: (archivedClassrooms: State['archivedClassrooms']) => void;
  setAreArchivedClassroomsLoading: (
    areArchivedClassroomsLoading: State['areArchivedClassroomsLoading']
  ) => void;
  setAreClassroomsLoading: (areClassroomsLoading: State['areClassroomsLoading']) => void;
  setArchivedClassroomsLoadedFor: (
    archivedClassroomsLoadedFor: State['archivedClassroomsLoadedFor']
  ) => void;
  setHasLoadedClassrooms: (hasLoadedClassrooms: State['hasLoadedClassrooms']) => void;
  setClassrooms: (classrooms: State['classrooms']) => void;
  setIsShowingArchivedClassrooms: (
    isShowingArchivedClassrooms: State['isShowingArchivedClassrooms']
  ) => void;
  setIsSidemenuCollapsed: (isSidemenuCollapsed: State['isSidemenuCollapsed']) => void;
  setSelectedClassId: (selectedClassId: State['selectedClassId']) => void;
  // Claims the remembered selection for an identity; clears it when the identity changed.
  syncSelectionOwner: (effectiveUserId: string) => void;
};

const initialState: State = {
  archivedClassrooms: List([]),
  areArchivedClassroomsLoading: false,
  archivedClassroomsLoadedFor: null,
  areClassroomsLoading: false,
  hasLoadedClassrooms: false,
  classrooms: List([]),
  isShowingArchivedClassrooms: false,
  isSidemenuCollapsed: startsCollapsed(),
  selectedClassId: null,
  selectionOwnerId: null
};

export const useTeacherDashboardStore = create<State & Actions>()(
  devtools(
    immer((set) => ({
      ...initialState,
      setArchivedClassrooms: (archivedClassrooms) => {
        set((state) => {
          state.archivedClassrooms = archivedClassrooms;
        });
      },
      setAreArchivedClassroomsLoading: (areArchivedClassroomsLoading) => {
        set((state) => {
          state.areArchivedClassroomsLoading = areArchivedClassroomsLoading;
        });
      },
      setAreClassroomsLoading: (areClassroomsLoading) => {
        set((state) => {
          state.areClassroomsLoading = areClassroomsLoading;
        });
      },
      setArchivedClassroomsLoadedFor: (archivedClassroomsLoadedFor) => {
        set((state) => {
          state.archivedClassroomsLoadedFor = archivedClassroomsLoadedFor;
        });
      },
      setHasLoadedClassrooms: (hasLoadedClassrooms) => {
        set((state) => {
          state.hasLoadedClassrooms = hasLoadedClassrooms;
        });
      },
      setIsShowingArchivedClassrooms: (isShowingArchivedClassrooms) => {
        set((state) => {
          state.isShowingArchivedClassrooms = isShowingArchivedClassrooms;
        });
      },
      setClassrooms: (classrooms) => {
        set((state) => {
          state.classrooms = classrooms;
        });
      },
      setIsSidemenuCollapsed: (isSidemenuCollapsed) => {
        set((state) => {
          state.isSidemenuCollapsed = isSidemenuCollapsed;
        });
      },
      setSelectedClassId: (selectedClassId) => {
        set((state) => {
          state.selectedClassId = selectedClassId;
        });
      },
      syncSelectionOwner: (effectiveUserId) => {
        set((state) => {
          if (state.selectionOwnerId !== effectiveUserId) {
            state.selectionOwnerId = effectiveUserId;
            state.selectedClassId = null;
          }
        });
      }
    }))
  )
);
